export type Page<T> = { total: number; items: T[] };

export type PageRequest = { offset: number; limit: number };

export const DEFAULT_PAGE_SIZE = 100;

// `total` is not trusted: a bad value would fan out one request per page.
const MAX_PAGES_PER_FETCH = 20;

type FetchPage<T> = (offset: number, limit: number) => Promise<Page<T>>;

const pageCountFor = (total: number, pageSize: number) => Math.min(Math.ceil(total / pageSize), MAX_PAGES_PER_FETCH);

const offsetsAfterFirstPage = (pageCount: number, pageSize: number) =>
  Array.from({ length: Math.max(pageCount - 1, 0) }, (_, index) => (index + 1) * pageSize);

export const fetchAllPages = async <T>(fetchPage: FetchPage<T>, pageSize = DEFAULT_PAGE_SIZE): Promise<T[]> => {
  const firstPage = await fetchPage(0, pageSize);
  const offsets = offsetsAfterFirstPage(pageCountFor(firstPage.total, pageSize), pageSize);
  const remainingPages = await Promise.all(offsets.map((offset) => fetchPage(offset, pageSize)));

  return [...firstPage.items, ...remainingPages.flatMap((page) => page.items)];
};
